import { useMemo, useCallback } from 'react';
import type { FilterGroup, DisplayContent } from './types';

export function useMultiSelect(
  groups: FilterGroup[],
  selectedItems: string[],
  onItemsChange: (items: string[]) => void,
  maxDisplayItems: number = 3
) {
  const allItems = useMemo(() => {
    return groups.flatMap((group) => group.items);
  }, [groups]);

  const selectedCount = selectedItems.length;
  const totalCount = allItems.length;
  const isAllSelected = selectedCount === totalCount && totalCount > 0;

  const handleSelectAll = useCallback(() => {
    onItemsChange(allItems.map((item) => item.id));
  }, [allItems, onItemsChange]);

  const handleClearAll = useCallback(() => {
    onItemsChange([]);
  }, [onItemsChange]);

  const getGroupSelectedCount = useCallback(
    (group: FilterGroup) => group.items.filter((item) => selectedItems.includes(item.id)).length,
    [selectedItems]
  );

  // Location groups run to a dozen warehouses and a few ports each, so picking
  // a whole town one row at a time was the tedious part (353334). Both act on
  // the entire group regardless of an active search — the search narrows what
  // you look at, not what the group is.
  const handleSelectGroup = useCallback(
    (group: FilterGroup) => {
      const groupIds = group.items.map((item) => item.id);

      onItemsChange([...selectedItems, ...groupIds.filter((id) => !selectedItems.includes(id))]);
    },
    [selectedItems, onItemsChange]
  );

  const handleClearGroup = useCallback(
    (group: FilterGroup) => {
      const groupIds = group.items.map((item) => item.id);

      onItemsChange(selectedItems.filter((id) => !groupIds.includes(id)));
    },
    [selectedItems, onItemsChange]
  );

  const handleToggleItem = useCallback(
    (itemId: string) => {
      if (selectedItems.includes(itemId)) {
        onItemsChange(selectedItems.filter((id) => id !== itemId));
      } else {
        onItemsChange([...selectedItems, itemId]);
      }
    },
    [selectedItems, onItemsChange]
  );

  const isItemSelected = useCallback(
    (itemId: string) => {
      return selectedItems.includes(itemId);
    },
    [selectedItems]
  );

  const getDisplayContent = useCallback((): DisplayContent => {
    if (selectedCount === 0) {
      return { type: 'placeholder' };
    }

    if (isAllSelected) {
      return { type: 'all' };
    }

    const selectedItemObjects = allItems.filter((item) => selectedItems.includes(item.id));
    const displayItems = selectedItemObjects.slice(0, maxDisplayItems);
    const remainingCount = selectedCount - displayItems.length;

    return {
      type: 'selected',
      displayItems: displayItems.map((item) => item.name),
      remainingCount
    };
  }, [selectedCount, isAllSelected, allItems, selectedItems, maxDisplayItems]);

  return {
    // Data
    allItems,
    selectedCount,
    totalCount,
    isAllSelected,

    // Actions
    handleSelectAll,
    handleClearAll,
    handleSelectGroup,
    handleClearGroup,
    handleToggleItem,
    getGroupSelectedCount,
    isItemSelected,

    displayContent: getDisplayContent()
  };
}
